/*
 * DAC: equal-width buttons in the hero slideshow.
 *
 * The slide buttons are plain inline siblings, each as wide as its own label. With a longer label on the left ("Shop
 * Sunglasses" next to "Shop Eyewear") the pair is no longer symmetrical and the left button crosses the centre line
 * into the other half of the split image. This gives every button of a slide the width of the widest one so the pair
 * is even and sits centred on the seam. Slides with a single button, or buttons in other places, are left alone.
 * It also holds the slide text back until that is done (see markReady).
 */
(() => {
  const SELECTOR = '.dac-slide-content';

  const equalise = (container) => {
    const buttons = Array.from(container.querySelectorAll(':scope > .button'));

    if (buttons.length < 2) {
      return;
    }

    // Measure the natural widths first, then apply the widest to all of them
    buttons.forEach((button) => {
      button.style.minWidth = '';
    });

    const widest = Math.max(...buttons.map((button) => button.getBoundingClientRect().width));

    if (widest > 0) {
      buttons.forEach((button) => {
        button.style.minWidth = `${Math.ceil(widest)}px`;
      });
    }
  };

  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver((entries) => {
    entries.forEach((entry) => equalise(entry.target));
  }) : null;

  const setup = () => {
    document.querySelectorAll(SELECTOR).forEach((container) => {
      equalise(container);

      if (observer && !container.dataset.dacButtonPairs) {
        container.dataset.dacButtonPairs = 'true';
        observer.observe(container);
      }
    });
  };

  setup();

  // The slide text and buttons stay hidden (theme.css) until the fonts are in and the buttons are equal, so they appear
  // once, in place, instead of loading uneven and snapping. html[data-dac-buttons-ready] shows them and the
  // "dac:buttons-ready" event lets the hero logo reveal start after them. 3s cap so nothing stays hidden.
  const markReady = () => {
    if (document.documentElement.hasAttribute('data-dac-buttons-ready')) {
      return;
    }

    setup();
    document.documentElement.setAttribute('data-dac-buttons-ready', '');
    document.dispatchEvent(new CustomEvent('dac:buttons-ready'));
  };

  // Wait only for the fonts the hero text and buttons use. document.fonts.ready is not that: in Safari it resolves only after
  // the whole page has loaded (this store's apps keep that busy for seconds), so on an iPhone the hero text and the logo
  // reveal sat hidden for the full 3s cap while Chrome showed them straight away.
  const heroFontsReady = () => {
    if (!document.fonts || !document.fonts.load) {
      return Promise.resolve();
    }

    const loads = [];

    document.querySelectorAll(SELECTOR).forEach((container) => {
      Array.from(container.children).forEach((element) => {
        const text = element.textContent.trim();

        if (text) {
          const style = getComputedStyle(element);
          loads.push(document.fonts.load(`${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`, text).catch(() => {}));
        }
      });
    });

    return Promise.all(loads);
  };

  Promise.race([heroFontsReady(), new Promise((resolve) => setTimeout(resolve, 3000))]).then(() => requestAnimationFrame(markReady));

  // Web fonts change the label widths when they arrive, and the theme editor re-renders sections
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(setup);
  }

  window.addEventListener('load', setup);
  document.addEventListener('shopify:section:load', setup);
})();
